"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { PixelText } from "@/components/ui/pixel-text";
import {
  ITEMS,
  type Item,
  type ItemCategory,
} from "@/engine/items";

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

export type InventoryEntry = {
  item: Item;
  count: number;
};

type PartyMember = {
  speciesId: string;
  nickname?: string;
  level: number;
  hpCurrent: number;
  hpMax: number;
  fainted: boolean;
};

type UseResultMessage = { ok: boolean; message: string };

interface InventoryScreenProps {
  open: boolean;
  inventory: Record<string, number>;
  party: PartyMember[];
  onUseItem: (
    itemId: string,
    targetPartyIndex?: number,
  ) => Promise<UseResultMessage>;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Style tokens (cream + ink, no neon, no rounded corners)
// ---------------------------------------------------------------------------

const CREAM = "#f5ecd6";
const INK = "#1a1a1a";
const DIM = "#8a8377";
const WALNUT = "#5c4a30";
const ALERT = "#c54a4a";
const MOSS = "#7a9a4a";
const MINT = "#bcd4a0";

// ---------------------------------------------------------------------------
// Categories
// ---------------------------------------------------------------------------

type TabKey = "ball" | "heal" | "misc" | "key";

const TABS: { key: TabKey; label: string; glyph: string }[] = [
  { key: "ball", label: "Balls", glyph: "\u25CF" },
  { key: "heal", label: "Heal", glyph: "+" },
  { key: "misc", label: "Misc", glyph: "*" },
  { key: "key", label: "Key Items", glyph: "\u25C6" },
];

// "Key Items" maps to flee + any future flag; for now we present items not in
// the other three visible tabs (catch_ball/heal/misc) — i.e. category === 'flee'.
function tabMatches(item: Item, tab: TabKey): boolean {
  if (tab === "ball") return item.category === "ball";
  if (tab === "heal") return item.category === "heal";
  if (tab === "misc") return item.category === "misc";
  // Key Items bucket.
  return item.category === "flee";
}

// ---------------------------------------------------------------------------
// Sound helper (no-op safe wrapper)
// ---------------------------------------------------------------------------

type SfxName = "menu_move" | "menu_select" | "item_pickup";

function playSfx(name: SfxName): void {
  if (typeof window === "undefined") return;
  const w = window as unknown as {
    playSfx?: (n: string) => void;
    surggiesSfx?: (n: string) => void;
  };
  try {
    if (typeof w.playSfx === "function") w.playSfx(name);
    else if (typeof w.surggiesSfx === "function") w.surggiesSfx(name);
  } catch {
    // sound is best-effort
  }
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function InventoryScreen({
  open,
  inventory,
  party,
  onUseItem,
  onClose,
}: InventoryScreenProps) {
  const [tabIndex, setTabIndex] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [targetingItemId, setTargetingItemId] = useState<string | null>(null);
  const [targetCursor, setTargetCursor] = useState(0);
  const [toast, setToast] = useState<UseResultMessage | null>(null);
  const [busy, setBusy] = useState(false);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeTab = TABS[tabIndex].key;

  // Build sorted entries for the active tab from the inventory prop.
  // We deliberately read every render so count updates appear immediately.
  const entries = useMemo<InventoryEntry[]>(() => {
    const ids = Object.keys(inventory);
    const list: InventoryEntry[] = [];
    for (const id of ids) {
      const item = ITEMS[id];
      if (!item) continue;
      const count = inventory[id] ?? 0;
      if (count <= 0) continue;
      if (!tabMatches(item, activeTab)) continue;
      list.push({ item, count });
    }
    list.sort((a, b) => a.item.name.localeCompare(b.item.name));
    return list;
  }, [inventory, activeTab]);

  // Per-category totals for the left tab strip.
  const tabCounts = useMemo<Record<TabKey, number>>(() => {
    const counts: Record<TabKey, number> = {
      ball: 0,
      heal: 0,
      misc: 0,
      key: 0,
    };
    for (const id of Object.keys(inventory)) {
      const item = ITEMS[id];
      if (!item) continue;
      const c = inventory[id] ?? 0;
      if (c <= 0) continue;
      for (const t of TABS) {
        if (tabMatches(item, t.key)) {
          counts[t.key] += 1;
          break;
        }
      }
    }
    return counts;
  }, [inventory]);

  // Reset cursor when tab changes.
  useEffect(() => {
    setCursor(0);
  }, [tabIndex]);

  // Clamp cursor when the entry list shrinks (e.g. last item used up).
  useEffect(() => {
    if (cursor > entries.length - 1) {
      setCursor(Math.max(0, entries.length - 1));
    }
  }, [entries.length, cursor]);

  // Reset state when closed/reopened.
  useEffect(() => {
    if (!open) {
      setTargetingItemId(null);
      setToast(null);
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
        toastTimer.current = null;
      }
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (toastTimer.current) clearTimeout(toastTimer.current);
    };
  }, []);

  const showToast = useCallback((msg: UseResultMessage) => {
    setToast(msg);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => {
      setToast(null);
      toastTimer.current = null;
    }, 1500);
  }, []);

  // -----------------------------------------------------------------------
  // Selecting / using items
  // -----------------------------------------------------------------------

  const itemNeedsTarget = useCallback((item: Item): boolean => {
    const eff = item.effect;
    if (!eff) return false;
    if (eff.revives) return true;
    if (typeof eff.healAmount === "number") return true;
    if (typeof eff.healPercent === "number") return true;
    if (eff.levelUp) return true;
    if (eff.teachesMoveId) return true;
    return false;
  }, []);

  const slotIsValidTarget = useCallback(
    (item: Item, member: PartyMember | undefined): boolean => {
      if (!member) return false;
      const revives = item.effect?.revives === true;
      if (revives) return member.fainted;
      // Heal-only / level / move TMs: must NOT be fainted.
      return !member.fainted;
    },
    [],
  );

  const performUse = useCallback(
    async (itemId: string, targetIdx?: number) => {
      if (busy) return;
      setBusy(true);
      try {
        playSfx("menu_select");
        const result = await onUseItem(itemId, targetIdx);
        if (result.ok) {
          const item = ITEMS[itemId];
          if (item && item.category === "heal") {
            playSfx("item_pickup");
          }
        }
        showToast(result);
      } catch (err) {
        showToast({
          ok: false,
          message:
            err instanceof Error ? err.message : "Something went wrong.",
        });
      } finally {
        setBusy(false);
        setTargetingItemId(null);
      }
    },
    [busy, onUseItem, showToast],
  );

  const beginUseFocused = useCallback(() => {
    const entry = entries[cursor];
    if (!entry) return;
    const { item, count } = entry;
    if (count <= 0) return;

    if (itemNeedsTarget(item)) {
      playSfx("menu_select");
      // Find first valid target slot to land cursor on.
      const firstValid = party.findIndex((m) => slotIsValidTarget(item, m));
      setTargetCursor(firstValid >= 0 ? firstValid : 0);
      setTargetingItemId(item.id);
      return;
    }

    void performUse(item.id);
  }, [entries, cursor, itemNeedsTarget, party, performUse, slotIsValidTarget]);

  // -----------------------------------------------------------------------
  // Keyboard
  // -----------------------------------------------------------------------

  const moveItemCursor = useCallback(
    (dir: 1 | -1) => {
      if (entries.length === 0) return;
      setCursor((c) => {
        const next = (c + dir + entries.length) % entries.length;
        if (next !== c) playSfx("menu_move");
        return next;
      });
    },
    [entries.length],
  );

  const switchTab = useCallback((dir: 1 | -1) => {
    setTabIndex((t) => {
      const next = (t + dir + TABS.length) % TABS.length;
      if (next !== t) playSfx("menu_move");
      return next;
    });
  }, []);

  const moveTargetCursor = useCallback(
    (dir: number) => {
      if (party.length === 0) return;
      setTargetCursor((c) => {
        const next = Math.max(0, Math.min(party.length - 1, c + dir));
        if (next !== c) playSfx("menu_move");
        return next;
      });
    },
    [party.length],
  );

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key;
      const handled = [
        "ArrowUp",
        "ArrowDown",
        "ArrowLeft",
        "ArrowRight",
        "Enter",
        "Tab",
        "z",
        "Z",
        "x",
        "X",
        "q",
        "Q",
        "e",
        "E",
        "Escape",
      ];
      if (handled.includes(key)) e.preventDefault();

      // Party-selector overlay has its own bindings.
      if (targetingItemId) {
        const focusedItem = ITEMS[targetingItemId];
        switch (key) {
          case "ArrowUp":
            moveTargetCursor(-2);
            break;
          case "ArrowDown":
            moveTargetCursor(2);
            break;
          case "ArrowLeft":
            moveTargetCursor(-1);
            break;
          case "ArrowRight":
            moveTargetCursor(1);
            break;
          case "Enter":
          case "z":
          case "Z": {
            const member = party[targetCursor];
            if (!focusedItem || !member) return;
            if (!slotIsValidTarget(focusedItem, member)) return;
            void performUse(focusedItem.id, targetCursor);
            break;
          }
          case "x":
          case "X":
          case "Escape":
            playSfx("menu_move");
            setTargetingItemId(null);
            break;
        }
        return;
      }

      switch (key) {
        case "ArrowUp":
          moveItemCursor(-1);
          break;
        case "ArrowDown":
          moveItemCursor(1);
          break;
        case "ArrowLeft":
        case "q":
        case "Q":
          switchTab(-1);
          break;
        case "ArrowRight":
        case "e":
        case "E":
          switchTab(1);
          break;
        case "Tab":
          switchTab(e.shiftKey ? -1 : 1);
          break;
        case "Enter":
        case "z":
        case "Z":
          beginUseFocused();
          break;
        case "x":
        case "X":
        case "Escape":
          onClose();
          break;
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    open,
    targetingItemId,
    targetCursor,
    party,
    beginUseFocused,
    moveItemCursor,
    moveTargetCursor,
    onClose,
    performUse,
    slotIsValidTarget,
    switchTab,
  ]);

  if (!open) return null;

  const focusedEntry = entries[cursor];
  const focusedItem = focusedEntry?.item;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Bag"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        backgroundColor: "rgba(26, 26, 26, 0.78)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        imageRendering: "pixelated",
        padding: "1.25rem",
      }}
      onClick={(e) => {
        // Click backdrop to close (but not while targeting — that gets its own).
        if (e.target === e.currentTarget && !targetingItemId) onClose();
      }}
    >
      <div
        style={{
          width: "min(64rem, 100%)",
          maxHeight: "100%",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          padding: "0.875rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.75rem",
          position: "relative",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.5rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="lg"
            className="!text-[#1a1a1a]"
          >
            BAG
          </PixelText>
          <button
            type="button"
            aria-label="Close bag"
            onClick={() => onClose()}
            className="font-mono font-bold uppercase tracking-widest"
            style={{
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              padding: "0.25rem 0.625rem",
              fontSize: "0.85rem",
              lineHeight: 1,
              cursor: "pointer",
              imageRendering: "pixelated",
            }}
          >
            X
          </button>
        </div>

        {/* Body: 3-pane grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "12rem 1fr 16rem",
            gap: "0.75rem",
            border: `2px solid ${INK}`,
            backgroundColor: CREAM,
          }}
        >
          {/* Left: category tabs */}
          <div
            role="tablist"
            aria-label="Item categories"
            style={{
              borderRight: `2px solid ${INK}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {TABS.map((tab, i) => {
              const active = i === tabIndex;
              const count = tabCounts[tab.key];
              return (
                <button
                  key={tab.key}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  onClick={() => {
                    if (!active) playSfx("menu_move");
                    setTabIndex(i);
                  }}
                  className="font-mono font-bold uppercase tracking-widest text-left"
                  style={{
                    backgroundColor: active ? INK : CREAM,
                    color: active ? CREAM : INK,
                    border: "none",
                    borderBottom: `2px solid ${INK}`,
                    padding: "0.625rem 0.75rem",
                    fontSize: "0.85rem",
                    lineHeight: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "0.5rem",
                    cursor: "pointer",
                    imageRendering: "pixelated",
                    transition: "none",
                  }}
                >
                  <span style={{ display: "flex", gap: "0.5rem" }}>
                    <span aria-hidden style={{ width: "1ch" }}>
                      {tab.glyph}
                    </span>
                    {tab.label}
                  </span>
                  <span
                    style={{
                      backgroundColor: active ? CREAM : INK,
                      color: active ? INK : CREAM,
                      padding: "0.125rem 0.375rem",
                      fontSize: "0.7rem",
                      lineHeight: 1,
                      minWidth: "1.25rem",
                      textAlign: "center",
                    }}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>

          {/* Center: item list */}
          <div
            role="listbox"
            aria-label={`${TABS[tabIndex].label} items`}
            style={{
              maxHeight: "22rem",
              overflowY: "auto",
              borderRight: `2px solid ${INK}`,
            }}
          >
            {entries.length === 0 ? (
              <div
                className="font-mono uppercase tracking-widest"
                style={{
                  color: DIM,
                  padding: "1rem 0.75rem",
                  fontSize: "0.8rem",
                  lineHeight: 1.2,
                }}
              >
                No items in this category.
              </div>
            ) : (
              entries.map((entry, i) => {
                const active = i === cursor;
                return (
                  <button
                    key={entry.item.id}
                    type="button"
                    role="option"
                    aria-selected={active}
                    onMouseEnter={() => setCursor(i)}
                    onClick={() => {
                      setCursor(i);
                      beginUseFocused();
                    }}
                    className="font-mono font-bold uppercase tracking-widest text-left w-full block relative"
                    style={{
                      backgroundColor: active ? INK : CREAM,
                      color: active ? CREAM : INK,
                      border: "none",
                      borderBottom: `2px solid ${INK}`,
                      padding: "0.5rem 0.75rem 0.5rem 1.5rem",
                      fontSize: "0.85rem",
                      lineHeight: 1,
                      cursor: "pointer",
                      imageRendering: "pixelated",
                      transition: "none",
                    }}
                  >
                    {active && (
                      <span
                        aria-hidden
                        style={{
                          position: "absolute",
                          left: "0.375rem",
                          top: "50%",
                          transform: "translateY(-50%)",
                          color: CREAM,
                          fontSize: "0.85rem",
                          lineHeight: 1,
                        }}
                      >
                        &#9654;
                      </span>
                    )}
                    <span style={{ display: "inline-block" }}>
                      {entry.item.name}
                    </span>
                    <span style={{ float: "right" }}>{`x${entry.count}`}</span>
                  </button>
                );
              })
            )}
          </div>

          {/* Right: detail panel */}
          <div
            style={{
              padding: "0.75rem",
              minHeight: "16rem",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              gap: "0.75rem",
            }}
            aria-live="polite"
          >
            {focusedItem ? (
              <>
                <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                  <PixelText
                    as="div"
                    glow="none"
                    size="md"
                    className="!text-[#1a1a1a] !tracking-normal !normal-case"
                  >
                    {focusedItem.name}
                  </PixelText>
                  <div
                    className="font-mono"
                    style={{
                      color: INK,
                      fontSize: "0.78rem",
                      lineHeight: 1.4,
                    }}
                  >
                    {focusedItem.description}
                  </div>
                  <div
                    className="font-mono uppercase tracking-widest"
                    style={{
                      color: WALNUT,
                      fontSize: "0.7rem",
                      lineHeight: 1,
                      marginTop: "0.25rem",
                    }}
                  >
                    Owned: {focusedEntry?.count ?? 0}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => beginUseFocused()}
                  disabled={busy || (focusedEntry?.count ?? 0) <= 0}
                  className="font-mono font-bold uppercase tracking-widest"
                  style={{
                    backgroundColor: INK,
                    color: CREAM,
                    border: `2px solid ${INK}`,
                    padding: "0.5rem 0.75rem",
                    fontSize: "0.85rem",
                    lineHeight: 1,
                    cursor:
                      busy || (focusedEntry?.count ?? 0) <= 0
                        ? "not-allowed"
                        : "pointer",
                    opacity: busy || (focusedEntry?.count ?? 0) <= 0 ? 0.5 : 1,
                    imageRendering: "pixelated",
                  }}
                >
                  Use
                </button>
              </>
            ) : (
              <div
                className="font-mono uppercase tracking-widest"
                style={{
                  color: DIM,
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                }}
              >
                Select an item.
              </div>
            )}
          </div>
        </div>

        {/* Bottom hint strip */}
        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: INK,
            fontSize: "0.7rem",
            lineHeight: 1,
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.5rem",
          }}
        >
          Z: Use &nbsp;|&nbsp; X: Back &nbsp;|&nbsp; {"\u2191\u2193"}: Navigate
          &nbsp;|&nbsp; Tab/Q/E: Categories
        </div>

        {/* Toast */}
        {toast && (
          <div
            role="status"
            aria-live="polite"
            style={{
              position: "absolute",
              left: "50%",
              bottom: "3rem",
              transform: "translateX(-50%)",
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              borderLeft: `6px solid ${toast.ok ? MOSS : ALERT}`,
              padding: "0.5rem 0.875rem",
              fontSize: "0.8rem",
              fontFamily: "var(--font-mono, ui-monospace)",
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              imageRendering: "pixelated",
              maxWidth: "32rem",
            }}
          >
            {toast.message}
          </div>
        )}

        {/* Party selector overlay */}
        {targetingItemId && (
          <PartySelector
            item={ITEMS[targetingItemId]}
            party={party}
            cursor={targetCursor}
            onHover={(i) => setTargetCursor(i)}
            onConfirm={(i) => {
              const it = ITEMS[targetingItemId];
              if (!it) return;
              if (!slotIsValidTarget(it, party[i])) return;
              void performUse(targetingItemId, i);
            }}
            onCancel={() => {
              playSfx("menu_move");
              setTargetingItemId(null);
            }}
            slotIsValidTarget={slotIsValidTarget}
            busy={busy}
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Party selector overlay (2x3 grid)
// ---------------------------------------------------------------------------

interface PartySelectorProps {
  item: Item | undefined;
  party: PartyMember[];
  cursor: number;
  onHover: (i: number) => void;
  onConfirm: (i: number) => void;
  onCancel: () => void;
  slotIsValidTarget: (item: Item, member: PartyMember | undefined) => boolean;
  busy: boolean;
}

function PartySelector({
  item,
  party,
  cursor,
  onHover,
  onConfirm,
  onCancel,
  slotIsValidTarget,
  busy,
}: PartySelectorProps) {
  if (!item) return null;

  // Pad to 6 slots for the 2x3 grid.
  const slots: (PartyMember | null)[] = [];
  for (let i = 0; i < 6; i++) slots.push(party[i] ?? null);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Choose target"
      style={{
        position: "absolute",
        inset: 0,
        backgroundColor: "rgba(26, 26, 26, 0.55)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "1rem",
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        style={{
          width: "min(36rem, 100%)",
          backgroundColor: CREAM,
          border: `2px solid ${INK}`,
          padding: "0.875rem",
          display: "flex",
          flexDirection: "column",
          gap: "0.625rem",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            borderBottom: `2px solid ${INK}`,
            paddingBottom: "0.375rem",
          }}
        >
          <PixelText
            as="div"
            glow="none"
            size="md"
            className="!text-[#1a1a1a] !tracking-normal !normal-case"
          >
            Use {item.name} on...
          </PixelText>
          <button
            type="button"
            aria-label="Cancel"
            onClick={onCancel}
            className="font-mono font-bold uppercase tracking-widest"
            style={{
              backgroundColor: CREAM,
              color: INK,
              border: `2px solid ${INK}`,
              padding: "0.2rem 0.5rem",
              fontSize: "0.75rem",
              lineHeight: 1,
              cursor: "pointer",
              imageRendering: "pixelated",
            }}
          >
            X
          </button>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, 1fr)",
            gridTemplateRows: "repeat(3, auto)",
            gap: "0.5rem",
          }}
        >
          {slots.map((member, i) => {
            const active = i === cursor;
            const valid = member ? slotIsValidTarget(item, member) : false;
            const present = !!member;
            return (
              <button
                key={i}
                type="button"
                aria-selected={active}
                aria-disabled={!valid}
                disabled={!valid || busy}
                onMouseEnter={() => {
                  if (present) onHover(i);
                }}
                onClick={() => {
                  if (!valid) return;
                  onConfirm(i);
                }}
                className="font-mono font-bold uppercase tracking-widest text-left"
                style={{
                  backgroundColor: active && valid ? INK : CREAM,
                  color: active && valid ? CREAM : INK,
                  border: `2px solid ${INK}`,
                  padding: "0.5rem",
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                  display: "flex",
                  flexDirection: "column",
                  gap: "0.375rem",
                  opacity: present ? (valid ? 1 : 0.5) : 0.35,
                  cursor: valid && !busy ? "pointer" : "not-allowed",
                  imageRendering: "pixelated",
                  transition: "none",
                  minHeight: "4.5rem",
                }}
              >
                {member ? (
                  <PartySlotInner
                    member={member}
                    active={active && valid}
                  />
                ) : (
                  <span
                    style={{
                      color: DIM,
                      fontSize: "0.7rem",
                    }}
                  >
                    -- Empty --
                  </span>
                )}
              </button>
            );
          })}
        </div>

        <div
          className="font-mono uppercase tracking-widest"
          style={{
            color: INK,
            fontSize: "0.7rem",
            lineHeight: 1,
            borderTop: `2px solid ${INK}`,
            paddingTop: "0.375rem",
          }}
        >
          Z: Confirm &nbsp;|&nbsp; X: Cancel &nbsp;|&nbsp;{" "}
          {"\u2190\u2191\u2192\u2193"}: Navigate
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Party slot inner content (sprite stand-in + level + HP bar)
// ---------------------------------------------------------------------------

function PartySlotInner({
  member,
  active,
}: {
  member: PartyMember;
  active: boolean;
}) {
  const pct = member.hpMax > 0 ? member.hpCurrent / member.hpMax : 0;
  const barColor =
    pct > 0.5 ? MOSS : pct > 0.2 ? "#c5a83a" : ALERT;
  const name = member.nickname || member.speciesId;
  const fg = active ? CREAM : INK;
  const trackBg = active ? WALNUT : "#d9cfb4";

  // Sprite path follows the manifest convention (char_surggie_*).
  const sprite = `/assets/surggies/char_${member.speciesId}.png`;

  return (
    <div style={{ display: "flex", gap: "0.5rem", alignItems: "center" }}>
      <div
        style={{
          width: "2rem",
          height: "2rem",
          border: `2px solid ${fg}`,
          backgroundColor: active ? WALNUT : MINT,
          flexShrink: 0,
          imageRendering: "pixelated",
          backgroundImage: `url(${sprite})`,
          backgroundSize: "cover",
          backgroundPosition: "top left",
          backgroundRepeat: "no-repeat",
        }}
        aria-hidden
      />
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "0.25rem",
          flex: 1,
          minWidth: 0,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            gap: "0.5rem",
            fontSize: "0.7rem",
            color: fg,
          }}
        >
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {name}
          </span>
          <span>Lv {member.level}</span>
        </div>
        <div
          style={{
            width: "100%",
            height: "0.4rem",
            backgroundColor: trackBg,
            border: `1px solid ${fg}`,
          }}
        >
          <div
            style={{
              width: `${Math.round(pct * 100)}%`,
              height: "100%",
              backgroundColor: member.fainted ? ALERT : barColor,
            }}
          />
        </div>
        <div
          style={{
            fontSize: "0.65rem",
            color: fg,
            lineHeight: 1,
          }}
        >
          {member.fainted
            ? "FAINTED"
            : `${member.hpCurrent}/${member.hpMax} HP`}
        </div>
      </div>
    </div>
  );
}

export default InventoryScreen;
